import { useMutation, useQueryClient } from '@tanstack/react-query';

import queryKeys from '@/configs/react-query-keys';
import { getApiErrorMessage } from '@/error-handler/api-error-messages/ApiErrorMessages';
import { ApiError } from '@/error-handler/ApiError';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import service from '@/services/index';
import { usePopup } from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';

import { buildTaxonomyFeedbackPath } from '../../utils/routes';

const useTaxonomyListAction = () => {
  const wr = useWorkspaceRouter();
  const queryClient = useQueryClient();
  const setPopup = usePopup();
  const addToast = useToast();
  const onItemClick = (alias: string) => {
    wr.push(buildTaxonomyFeedbackPath(alias));
  };

  const toggleStar = useMutation({
    mutationKey: queryKeys.taxonomy.TAXONOMY_LIST,
    mutationFn: (alias: string) =>
      service.taxonomy.toggleTaxonomyItemStar(alias),
    async onMutate(alias: string) {
      await queryClient.cancelQueries({
        queryKey: queryKeys.taxonomy.TAXONOMY_LIST,
      });

      const previousData: TaxonomyT[] | undefined = queryClient.getQueryData(
        queryKeys.taxonomy.TAXONOMY_LIST,
      );

      if (!previousData) return { previousData };

      const target = previousData.find((item) => item.alias === alias);

      if (!target) return { previousData };

      const newTarget = {
        ...target,
        starred: !target.starred,
      };

      if (!target.starred) {
        addToast('Taxonomy starred', 'success');
      } else {
        addToast('Taxonomy unstarred', 'success');
      }

      queryClient.setQueryData(
        queryKeys.taxonomy.TAXONOMY_LIST,
        previousData.upsert(newTarget, (taxonomy) => taxonomy.alias === alias),
      );

      return {
        previousData,
      };
    },
    onError: (err, newData, context) => {
      queryClient.setQueryData(
        queryKeys.taxonomy.TAXONOMY_LIST,
        context?.previousData,
      );
    },
  });

  const onDeleteClick = (alias: string) => {
    setPopup({
      type: 'confirm',
      popupTheme: 'negative',
      title: 'Delete Taxonomy',
      message: `Are you sure to delete taxonomy?`,
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await service.taxonomy.deleteTaxonomyItem(alias);
          queryClient.invalidateQueries({
            queryKey: queryKeys.taxonomy.TAXONOMY_LIST,
          });
          addToast('Delete taxonomy successfully', 'success');
        } catch (err) {
          if (err instanceof ApiError) {
            addToast(getApiErrorMessage(err.data.client_code), 'fail');
            return;
          }

          addToast('Failed to delete taxonomy', 'fail');
        }
      },
    });
  };

  const onStarClick = toggleStar.mutateAsync;

  return {
    onDeleteClick,
    onStarClick,
    onItemClick,
  };
};

export default useTaxonomyListAction;
